import { IntervalKey, PeriodKey } from './types';

export const CHART_SOURCE = 'labci-chart5';

export type ChartSymbolInfo = { timeZone: string };
export type ChartCodeInfo = {
  dp: number;
  timezone?: string;
  tradeSessions?: Record<number, { startTime: string; endTime: string }>;
};
export type ChartInfoData = Record<
  string,
  ChartSymbolInfo | Record<string, ChartCodeInfo>
> & {
  info: Record<string, ChartCodeInfo>;
};

export type ChartPeriodType = 'hts' | 'mts';

export interface ChartBundlePayload {
  source: 'labci-chart5';
  ric: string;
  chartType: string;
  periodType: ChartPeriodType;
  chartData: string;
  infoData: string;
  interval: string;
  period: string;
  periodChange: boolean;
  lang: string;
  theme: string;
  drag2Slide: boolean;
  showFrom: string;
  showTo: string;
  showMainChart: boolean;
  showTA: boolean;
  showScrollView: boolean;
  showCrosshair: boolean;
  showDrawTool: boolean;
  showCompare: boolean;
  showToolTips: boolean;
  showTracker: boolean;
  overlay: string;
  indicator: string;
  chartSetting: Record<string, unknown>;
  chart5Setting?: Record<string, unknown>;
  chartOption: Record<string, unknown>;
  drawToolOption?: Record<string, unknown>;
  compareData?: unknown[];
}

export function getPeriodType(interval: IntervalKey): ChartPeriodType {
  return interval === '1' ||
    interval === '5' ||
    interval === '15' ||
    interval === '60'
    ? 'mts'
    : 'hts';
}

const MTS_TRADE_SESSIONS: Record<
  number,
  { startTime: string; endTime: string }
> = {
  1: { startTime: '10:00', endTime: '16:00' },
};
const TIMEZONE = 'Australia/Sydney';

export function buildEncodedInfoData(symbol: string, isMts = false): string {
  const info: ChartInfoData = {
    [symbol]: { timeZone: TIMEZONE },
    info: {
      [symbol]: {
        dp: 2,
        timezone: TIMEZONE,
        ...(isMts ? { tradeSessions: MTS_TRADE_SESSIONS } : {}),
      },
    },
  };
  return encodeURIComponent(JSON.stringify(info));
}

const EMPTY_ENCODED = encodeURIComponent('{}');

const LIGHT_THEME_SETTING = {
  backgroundColor: 'FFFFFF',
  chartBackgroundColor: 'FFFFFF',
  chartLineColor: 'C6D1CC',
  chartLineStyle: 's',
  toolTipsColor: '67727A',
  crosshairLineColor: '444444',
  crosshairLineStyle: 's',
  crosshairPointColor: '444444',
  crosshairPointSize: '4',
  crosshairFontColor: 'FFFFFF',
  priceTrackColor: 'D44500',
  priceTrackFontColor: 'FFFFFF',
  priceTrackThinkness: 2,
  priceTrackStyle: 'd',
  yAsis: false,
};

export const DEFAULT_CHART_OPTION = {
  barColor: '2b3c8f',
  candleWidth: 15,
  downColor: 'b71c15',
  downFillColor: 'b71c15',
  histogramDownColor: 'DDDDDD',
  histogramDownFillColor: '99CCFF',
  histogramUpColor: 'DDDDDD',
  histogramUpFillColor: '99CCFF',
  lineColor: '8B73B6',
  lineFillColor: '8B73B6',
  lineFillColor2: 'FFFFFF',
  lineGradientEnable: true,
  lineStyle: 's',
  lineThickness: 2,
  upColor: '1c7d45',
  upFillColor: '1c7d45',
};

export function csvEdgeDates(csv: string): {
  showFrom: string;
  showTo: string;
} {
  const rows = (csv ?? '').split('\n').filter(Boolean);
  if (rows.length === 0) return { showFrom: '', showTo: '' };
  const pad = (ts: string) => (ts.length === 8 ? ts + '0000' : ts);
  return {
    showFrom: pad(rows[0].split(',')[0]),
    showTo: pad(rows[rows.length - 1].split(',')[0]),
  };
}

export function getShowFromByPeriod(period: PeriodKey, showTo: string): string {
  if (!showTo) return '';
  const year = parseInt(showTo.slice(0, 4), 10);
  const month = parseInt(showTo.slice(4, 6), 10) - 1;
  const day = parseInt(showTo.slice(6, 8), 10);
  const toDate = new Date(year, month, day);

  const periodMap: Record<string, () => Date> = {
    '1D': () => {
      const d = new Date(toDate);
      d.setDate(d.getDate() - 1);
      return d;
    },
    '2D': () => {
      const d = new Date(toDate);
      d.setDate(d.getDate() - 2);
      return d;
    },
    '5D': () => {
      const d = new Date(toDate);
      d.setDate(d.getDate() - 5);
      return d;
    },
    MTD: () => new Date(toDate.getFullYear(), toDate.getMonth(), 1),
    '1M': () => {
      const d = new Date(toDate);
      d.setMonth(d.getMonth() - 1);
      return d;
    },
    '2M': () => {
      const d = new Date(toDate);
      d.setMonth(d.getMonth() - 2);
      return d;
    },
    '3M': () => {
      const d = new Date(toDate);
      d.setMonth(d.getMonth() - 3);
      return d;
    },
    '6M': () => {
      const d = new Date(toDate);
      d.setMonth(d.getMonth() - 6);
      return d;
    },
    YTD: () => new Date(toDate.getFullYear(), 0, 1),
    '1Y': () => {
      const d = new Date(toDate);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    },
    '2Y': () => {
      const d = new Date(toDate);
      d.setFullYear(d.getFullYear() - 2);
      return d;
    },
    '3Y': () => {
      const d = new Date(toDate);
      d.setFullYear(d.getFullYear() - 3);
      return d;
    },
    '4Y': () => {
      const d = new Date(toDate);
      d.setFullYear(d.getFullYear() - 4);
      return d;
    },
    '5Y': () => {
      const d = new Date(toDate);
      d.setFullYear(d.getFullYear() - 5);
      return d;
    },
    '10Y': () => {
      const d = new Date(toDate);
      d.setFullYear(d.getFullYear() - 10);
      return d;
    },
  };

  const calcFn = periodMap[period];
  if (!calcFn) return showTo;

  const fromDate = calcFn();
  const y = fromDate.getFullYear().toString();
  const m = String(fromDate.getMonth() + 1).padStart(2, '0');
  const dd = String(fromDate.getDate()).padStart(2, '0');
  return `${y}${m}${dd}0000`;
}

export function buildChartPayload(
  symbol: string,
  interval: IntervalKey,
  period: PeriodKey,
  chartType: string,
  overrides?: Partial<ChartBundlePayload>,
): ChartBundlePayload {
  const isMts = getPeriodType(interval) === 'mts';

  return {
    source: CHART_SOURCE,
    ric: symbol,
    chartType,
    periodType: getPeriodType(interval),
    chartData: '',
    infoData: buildEncodedInfoData(symbol, isMts),
    interval,
    period,
    periodChange: true,
    lang: 'en',
    theme: 'light',
    drag2Slide: true,
    showFrom: '',
    showTo: '',
    showMainChart: true,
    showTA: true,
    showScrollView: true,
    showCrosshair: true,
    showDrawTool: true,
    showCompare: false,
    showToolTips: false,
    showTracker: false,
    overlay: EMPTY_ENCODED,
    indicator: EMPTY_ENCODED,
    chartSetting: LIGHT_THEME_SETTING,
    chart5Setting: LIGHT_THEME_SETTING,
    chartOption: DEFAULT_CHART_OPTION,
    ...overrides,
  };
}
